import crypto from "crypto";
import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { formatToTimeZone } from "date-fns-timezone";
import { twMerge } from "tailwind-merge";

const algorithm = "aes-256-cbc";
const secretKey = Buffer.from("856Q0HFgvN0JAmnNBSbSF5kDNVpJ1ymD", "utf-8");
const BYTES = 16;

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function getPathSegments(pathname: string): string[] {
  return pathname.split("/").filter((segment) => segment);
}

export function getBreadcrumbLabel(segment: string): string {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getUnderScoreLabel(segment: string): string {
  return segment
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getCollectionDateTime(dateStr: string, timeStr: string) {
  const segment = timeStr.slice(-2);
  const timeHM = timeStr.split(segment)[0]?.split(":");
  let time = `${timeHM?.[0]}:${timeHM?.[1]}`;

  if (segment === "pm") {
    if (timeHM?.[0] === "12") {
      time = `12:${timeHM[1]}`;
    } else {
      const hour = parseInt(timeHM?.[0] ?? "0") + 12;
      time = `${hour}:${timeHM?.[1]}`;
    }
  } else {
    if (timeHM?.[0] === "12") {
      time = `00:${timeHM[1]}`;
    }
  }

  return `${dateStr} ${time}`;
}

export function formatAMPM(date: Date, timeZone: string) {
  let hours = Number(formatToTimeZone(date, "HH", { timeZone }));
  let minutes = formatToTimeZone(date, "mm", { timeZone });
  let ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  let strTime = hours + ":" + minutes + ampm;
  return strTime;
}

export function getBreadcrumbPath(
  pathSegments: string[],
  index: number,
): string {
  return pathSegments
    .slice(0, index + 1)
    .map((segment) => {
      if (
        segment.includes("%") ||
        segment.includes("?") ||
        segment.includes(":")
      )
        return segment;
      return encodeURIComponent(
        segment
          .split(" ")
          .map((word) => word.charAt(0).toLocaleLowerCase() + word.slice(1))
          .join("-"),
      );
    })
    .join("/");
}

export function generateURL(name: string): string {
  return encodeURIComponent(name).replace(/-/g, "%2D");
}

export function getTitleFromUrl(url: string): string {
  return decodeURIComponent(url);
}

export function getBackURL(pathname: string | null) {
  const splitPathname = pathname?.split("/");
  splitPathname?.pop();
  return splitPathname?.join("/");
}

export function pathToCamelCase(pathname: string | null) {
  const splitPathname = pathname?.split("-");
  const pathCamel = `${splitPathname?.[0]}${splitPathname?.[1]
    ?.charAt(0)
    .toUpperCase()}${splitPathname?.[1]?.slice(1)}`;
  return pathCamel;
}

export function flatToCamelCase(pathname: string | null) {
  const splitPathname = pathname?.split(" ");
  if (splitPathname?.length === 1) {
    return splitPathname[0]?.toLowerCase() ?? "";
  }

  let strCamel = "";
  for (let i = 0; i < (splitPathname?.length ?? 0); i++) {
    if (i === 0) {
      strCamel += splitPathname?.[i]?.toLowerCase();
    } else {
      strCamel += `${splitPathname?.[i]?.charAt(0).toUpperCase()}${splitPathname?.[i]?.slice(1).toLowerCase()}`;
    }
  }
  return strCamel;
}

export function strCamelToFlat(strCamel: string) {
  const camelCase = strCamel.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");

  let flat = "";

  camelCase.forEach((word) => {
    flat = flat + word.charAt(0).toUpperCase() + word.slice(1) + " ";
  });
  return flat;
}

export function strSnakeToFlat(str?: string) {
  if (str) {
    const titleCase = str?.replace(/^_*(.)|_+(.)/g, (str, c, d) =>
      c ? c.toUpperCase() : " " + d.toUpperCase(),
    );
    return titleCase;
  } else {
    return str;
  }
}

export function strSnakeToPascal(str: string) {
  const titleCase = str.replace(/^_*(.)|_+(.)/g, (str, c, d) =>
    c ? c.toUpperCase() : "-" + d,
  );
  return titleCase;
}

export function strCamelToDash(key: string) {
  var result = key.replace(/([A-Z])/g, " $1");
  return result.split(" ").join("-").toLowerCase();
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function flattenObject(obj: any, parentKey = ""): Record<string, any> {
  return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
    const prefixedKey = parentKey ? `${parentKey}.${key}` : key;
    if (
      typeof obj[key] === "object" &&
      obj[key] !== null &&
      !Array.isArray(obj[key]) &&
      Object.keys(obj[key]).length > 0
    ) {
      Object.assign(acc, flattenObject(obj[key], prefixedKey));
    } else {
      acc[prefixedKey] = obj[key];
    }
    return acc;
  }, {});
}

// Flatten object and check if there's any non-falsy value.
export function objectHasLeafValues(obj: any) {
  const flatObject = flattenObject(obj);
  return Object.values(flatObject).some((v) => v);
}

export function encrypt(text?: string): string {
  if (!text) return "";
  const iv = crypto.randomBytes(BYTES);

  const cipher = crypto.createCipheriv(algorithm, secretKey, iv);
  let encrypted = cipher.update(text);
  encrypted = Buffer.concat([encrypted, cipher.final()]);

  return `${iv.toString("hex")}:${encrypted.toString("hex")}`;
}

export function decrypt(textBase64: string | null): string | null {
  if (!textBase64) return null;

  const textParts = textBase64.split(":");
  const iv = Buffer.from(textParts.shift()!, "hex");
  const encryptedText = Buffer.from(textParts.join(":"), "hex");

  const decipher = crypto.createDecipheriv(algorithm, secretKey, iv);
  let decrypted = decipher.update(encryptedText);
  decrypted = Buffer.concat([decrypted, decipher.final()]);

  return decrypted.toString();
}
